import { useEffect, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ROLES_LABEL } from "@/lib/format";
import { FileUpload } from "@/components/FileUpload";
import { fileUrl, downloadExcel } from "@/lib/files";
import { Plus, Building2, Tags, Users, Trash2, Pencil, Download, Save, Upload, Power } from "lucide-react";
import { toast } from "sonner";

export default function Parametres() {
  const { user } = useAuth();
  const { loadSocietes } = useApp();
  const [societes, setSocietes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);

  const [socOpen, setSocOpen] = useState(false);
  const [socForm, setSocForm] = useState(null);
  const [catName, setCatName] = useState("");
  const [userOpen, setUserOpen] = useState(false);
  const [userForm, setUserForm] = useState(null);
  const [socToToggle, setSocToToggle] = useState(null);
  const restoreRef = useRef(null);

  const isAdmin = user?.role === "administrateur";
  const canManageUsers = isAdmin || user?.role === "direction";

  const loadAll = () => {
    api.get("/societes", { params: isAdmin ? { include_inactive: true } : {} }).then((r) => setSocietes(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    if (canManageUsers) api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []); // eslint-disable-line

  // Sauvegarde / Restauration (admin)
  const downloadBackup = async () => {
    try {
      const res = await api.get("/backup/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sauvegarde-generalfood-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Sauvegarde téléchargée. Enregistrez-la sur votre disque ou clé USB.");
    } catch (e) { toast.error("Impossible de générer la sauvegarde."); }
  };
  const restoreBackup = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!window.confirm("Restaurer cette sauvegarde ? Les données actuelles seront remplacées par celles du fichier.")) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backup/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const n = Object.values(r.data?.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Restauration terminée : ${n} enregistrement(s) importé(s).`);
      loadAll(); loadSocietes();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleSocActive = async () => {
    const s = socToToggle;
    setSocToToggle(null);
    if (!s) return;
    try {
      if (s.active === false) await api.post(`/societes/${s.id}/activate`);
      else await api.delete(`/societes/${s.id}`);
      toast.success(s.active === false ? "Société réactivée." : "Société désactivée.");
      loadAll(); loadSocietes();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Sociétés
  const openSoc = () => { setSocForm({ name: "", nom_commercial: "", rc: "", nif: "", nis: "", article: "", nin: "", adresse: "", telephone: "", email: "", devise: "DA", exercice: 2026, prefixe_depense: "DEP", prefixe_facture: "FAC", timbre_taux: 1, timbre_plafond: 2500, logo: null, active: true }); setSocOpen(true); };
  const editSoc = (s) => { setSocForm({ ...s, logo: s.logo_id ? { id: s.logo_id, filename: s.logo_nom || "logo" } : null }); setSocOpen(true); };
  const saveSoc = async () => {
    if (!socForm.name) return toast.error("Nom requis.");
    try {
      const payload = { ...socForm, exercice: Number(socForm.exercice), timbre_taux: Number(socForm.timbre_taux) || 0, timbre_plafond: Number(socForm.timbre_plafond) || 0, logo_id: socForm.logo?.id || null, logo_nom: socForm.logo?.filename || null };
      if (socForm.id) await api.put(`/societes/${socForm.id}`, payload);
      else await api.post("/societes", payload);
      toast.success(socForm.id ? "Société mise à jour." : "Société créée.");
      setSocOpen(false); loadAll(); loadSocietes();
    } catch (e) { toast.error(apiError(e)); }
  };

  // Catégories
  const addCat = async () => {
    if (!catName.trim()) return;
    try {
      await api.post("/categories", { name: catName.trim() });
      setCatName(""); loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delCat = async (id) => { try { await api.delete(`/categories/${id}`); loadAll(); } catch (e) { toast.error(apiError(e)); } };

  // Utilisateurs
  const openUser = () => { setUserForm({ email: "", password: "", name: "", role: "comptabilite", societe_ids: societes.map((s) => s.id), active: true }); setUserOpen(true); };
  const openEditUser = (u) => { setUserForm({ id: u.id, email: u.email, password: "", name: u.name, role: u.role, societe_ids: u.societe_ids || [], active: u.active }); setUserOpen(true); };
  const toggleSoc = (sid) => setUserForm((f) => ({ ...f, societe_ids: f.societe_ids.includes(sid) ? f.societe_ids.filter((x) => x !== sid) : [...f.societe_ids, sid] }));
  const saveUser = async () => {
    if (!userForm.email || !userForm.name) return toast.error("Email et nom requis.");
    if (!userForm.id && !userForm.password) return toast.error("Mot de passe requis.");
    if (!userForm.societe_ids.length) return toast.error("Autorisez au moins une société.");
    try {
      if (userForm.id) { await api.put(`/users/${userForm.id}`, userForm); toast.success("Utilisateur mis à jour."); }
      else { await api.post("/users", userForm); toast.success("Utilisateur créé."); }
      setUserOpen(false); loadAll();
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { ...u, password: "", active: !u.active }); loadAll(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Paramètres" subtitle="Sociétés, catégories et utilisateurs" testid="parametres-page"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" className="gap-2" onClick={() => downloadExcel("/export/global", "sauvegarde-globale")} data-testid="export-global-btn"><Download className="h-4 w-4" /> Export Excel</Button>
            {isAdmin && <Button variant="outline" className="gap-2" onClick={downloadBackup} data-testid="backup-json-btn"><Save className="h-4 w-4" /> Sauvegarde (disque / clé USB)</Button>}
            {isAdmin && <Button variant="outline" className="gap-2" onClick={() => restoreRef.current?.click()} data-testid="restore-json-btn"><Upload className="h-4 w-4" /> Restaurer</Button>}
            <input ref={restoreRef} type="file" accept="application/json,.json" className="hidden" onChange={restoreBackup} data-testid="restore-file-input" />
          </div>
        } />

      <Tabs defaultValue="societes">
        <TabsList data-testid="settings-tabs">
          <TabsTrigger value="societes" className="gap-1.5"><Building2 className="h-4 w-4" /> Sociétés</TabsTrigger>
          <TabsTrigger value="categories" className="gap-1.5"><Tags className="h-4 w-4" /> Catégories</TabsTrigger>
          {canManageUsers && <TabsTrigger value="users" className="gap-1.5"><Users className="h-4 w-4" /> Utilisateurs</TabsTrigger>}
        </TabsList>

        {/* Sociétés */}
        <TabsContent value="societes" className="mt-4">
          <div className="flex justify-end mb-3">
            <Button onClick={openSoc} className="gap-2" data-testid="new-societe-btn"><Plus className="h-4 w-4" /> Nouvelle société</Button>
          </div>
          <div className="rounded-xl border bg-card overflow-hidden">
            <Table>
              <TableHeader><TableRow className="bg-muted/50"><TableHead>Logo</TableHead><TableHead>Nom</TableHead><TableHead>RC</TableHead><TableHead>NIF</TableHead><TableHead>Exercice</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {societes.map((s) => (
                  <TableRow key={s.id} className={s.active === false ? "opacity-60" : ""} data-testid={`soc-row-${s.id}`}>
                    <TableCell>{s.logo_id ? <img src={fileUrl(s.logo_id)} alt="logo" className="h-9 w-9 rounded-md object-contain border bg-white" /> : <div className="h-9 w-9 rounded-md bg-muted flex items-center justify-center"><Building2 className="h-4 w-4 text-muted-foreground" /></div>}</TableCell>
                    <TableCell className="font-medium">{s.name}<p className="text-xs text-muted-foreground">{s.nom_commercial}</p></TableCell>
                    <TableCell className="tabular text-sm">{s.rc || "—"}</TableCell>
                    <TableCell className="tabular text-sm">{s.nif || "—"}</TableCell>
                    <TableCell className="tabular text-sm">{s.exercice}</TableCell>
                    <TableCell>{s.active === false ? <Badge variant="secondary" className="font-normal text-muted-foreground">Désactivée</Badge> : <span className="text-xs text-success">Active</span>}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 justify-end">
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => editSoc(s)} data-testid={`edit-soc-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                        {isAdmin && (
                          s.active === false
                            ? <Button size="sm" variant="ghost" className="text-xs gap-1 text-success" onClick={() => setSocToToggle(s)} data-testid={`activate-soc-${s.id}`}><Power className="h-3.5 w-3.5" /> Réactiver</Button>
                            : <Button size="sm" variant="ghost" className="text-xs gap-1 text-destructive" onClick={() => setSocToToggle(s)} data-testid={`deactivate-soc-${s.id}`}><Power className="h-3.5 w-3.5" /> Désactiver</Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Catégories */}
        <TabsContent value="categories" className="mt-4">
          <div className="flex gap-2 mb-4 max-w-md">
            <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Nouvelle catégorie" data-testid="cat-input" onKeyDown={(e) => e.key === "Enter" && addCat()} />
            <Button onClick={addCat} className="gap-2 shrink-0" data-testid="add-cat-btn"><Plus className="h-4 w-4" /> Ajouter</Button>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <Badge key={c.id} variant="secondary" className="gap-1.5 pl-3 pr-1.5 py-1.5 text-sm font-normal">
                {c.name}
                <button onClick={() => delCat(c.id)} className="hover:text-destructive" data-testid={`del-cat-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </Badge>
            ))}
          </div>
        </TabsContent>

        {/* Utilisateurs */}
        {canManageUsers && (
          <TabsContent value="users" className="mt-4">
            <div className="flex justify-end mb-3">
              {isAdmin && <Button onClick={openUser} className="gap-2" data-testid="new-user-btn"><Plus className="h-4 w-4" /> Nouvel utilisateur</Button>}
            </div>
            <div className="rounded-xl border bg-card overflow-hidden">
              <Table>
                <TableHeader><TableRow className="bg-muted/50"><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Sociétés autorisées</TableHead><TableHead>Statut</TableHead><TableHead></TableHead></TableRow></TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><Badge variant="secondary" className="font-normal">{ROLES_LABEL[u.role]}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{u.role === "administrateur" ? "Toutes" : `${(u.societe_ids || []).length} société(s)`}</TableCell>
                      <TableCell>{u.active ? <span className="text-xs text-success">Actif</span> : <span className="text-xs text-muted-foreground">Inactif</span>}</TableCell>
                      <TableCell className="text-right">
                        {isAdmin && (
                          <div className="flex gap-1 justify-end">
                            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditUser(u)} data-testid={`edit-user-${u.id}`}><Pencil className="h-4 w-4" /></Button>
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`}>{u.active ? "Désactiver" : "Activer"}</Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Société dialog */}
      <Dialog open={socOpen} onOpenChange={setSocOpen}>
        <DialogContent className="bg-popover max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="societe-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{socForm?.id ? "Modifier la société" : "Nouvelle société"}</DialogTitle></DialogHeader>
          {socForm && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Logo de la société</Label>
                <div className="flex items-center gap-3">
                  {socForm.logo?.id && <img src={fileUrl(socForm.logo.id)} alt="logo" className="h-14 w-14 rounded-md object-contain border bg-white shrink-0" />}
                  <div className="flex-1"><FileUpload value={socForm.logo} onChange={(v) => setSocForm((f) => ({ ...f, logo: v }))} testid="soc-logo" /></div>
                </div>
              </div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Nom / Raison sociale *</Label><Input data-testid="soc-name" value={socForm.name} onChange={(e) => setSocForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Nom commercial</Label><Input value={socForm.nom_commercial} onChange={(e) => setSocForm((f) => ({ ...f, nom_commercial: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Exercice</Label><Input type="number" value={socForm.exercice} onChange={(e) => setSocForm((f) => ({ ...f, exercice: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>RC</Label><Input value={socForm.rc} onChange={(e) => setSocForm((f) => ({ ...f, rc: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>NIF</Label><Input value={socForm.nif} onChange={(e) => setSocForm((f) => ({ ...f, nif: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>NIS</Label><Input value={socForm.nis} onChange={(e) => setSocForm((f) => ({ ...f, nis: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>N° Article</Label><Input value={socForm.article || ""} onChange={(e) => setSocForm((f) => ({ ...f, article: e.target.value }))} data-testid="soc-article" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>NIN gérant (N° identifiant national)</Label><Input value={socForm.nin || ""} onChange={(e) => setSocForm((f) => ({ ...f, nin: e.target.value }))} data-testid="soc-nin" /></div>
              <div className="space-y-1.5"><Label>Téléphone</Label><Input value={socForm.telephone} onChange={(e) => setSocForm((f) => ({ ...f, telephone: e.target.value }))} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label>Adresse</Label><Input value={socForm.adresse} onChange={(e) => setSocForm((f) => ({ ...f, adresse: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Préfixe dépense</Label><Input value={socForm.prefixe_depense} onChange={(e) => setSocForm((f) => ({ ...f, prefixe_depense: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Préfixe facture</Label><Input value={socForm.prefixe_facture} onChange={(e) => setSocForm((f) => ({ ...f, prefixe_facture: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Taux timbre (%)</Label><Input type="number" step="0.1" value={socForm.timbre_taux ?? 1} onChange={(e) => setSocForm((f) => ({ ...f, timbre_taux: e.target.value }))} data-testid="soc-timbre-taux" /></div>
              <div className="space-y-1.5"><Label>Plafond timbre (DA)</Label><Input type="number" value={socForm.timbre_plafond ?? 2500} onChange={(e) => setSocForm((f) => ({ ...f, timbre_plafond: e.target.value }))} data-testid="soc-timbre-plafond" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSocOpen(false)}>Annuler</Button>
            <Button onClick={saveSoc} data-testid="soc-submit">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User dialog */}
      <Dialog open={userOpen} onOpenChange={setUserOpen}>
        <DialogContent className="bg-popover max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">{userForm?.id ? "Modifier l'utilisateur" : "Nouvel utilisateur"}</DialogTitle></DialogHeader>
          {userForm && (
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-1.5"><Label>Nom complet *</Label><Input data-testid="user-name" value={userForm.name} onChange={(e) => setUserForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>E-mail *</Label><Input data-testid="user-email" type="email" value={userForm.email} onChange={(e) => setUserForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{userForm.id ? "Nouveau mot de passe (laisser vide pour conserver)" : "Mot de passe *"}</Label><Input data-testid="user-password" type="password" value={userForm.password} onChange={(e) => setUserForm((f) => ({ ...f, password: e.target.value }))} /></div>
              <div className="space-y-1.5">
                <Label>Rôle</Label>
                <Select value={userForm.role} onValueChange={(v) => setUserForm((f) => ({ ...f, role: v }))}>
                  <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    {Object.entries(ROLES_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Sociétés autorisées (accès)</Label>
                {userForm.role === "administrateur" ? (
                  <p className="text-xs text-muted-foreground">L'administrateur a accès à toutes les sociétés.</p>
                ) : (
                  <div className="space-y-1.5 rounded-lg border p-3 max-h-44 overflow-y-auto" data-testid="user-societes">
                    {societes.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`user-soc-${s.id}`}>
                        <input type="checkbox" className="h-4 w-4 accent-primary" checked={userForm.societe_ids.includes(s.id)} onChange={() => toggleSoc(s.id)} />
                        <span>{s.name}</span>
                      </label>
                    ))}
                    {societes.length === 0 && <p className="text-xs text-muted-foreground">Aucune société.</p>}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserOpen(false)}>Annuler</Button>
            <Button onClick={saveUser} data-testid="user-submit">{userForm?.id ? "Enregistrer" : "Créer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation désactivation / réactivation société */}
      <AlertDialog open={!!socToToggle} onOpenChange={(o) => !o && setSocToToggle(null)}>
        <AlertDialogContent data-testid="soc-toggle-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{socToToggle?.active === false ? "Réactiver la société ?" : "Désactiver la société ?"}</AlertDialogTitle>
            <AlertDialogDescription>
              {socToToggle?.active === false
                ? <>La société « {socToToggle?.name} » redeviendra visible et sélectionnable.</>
                : <>La société « {socToToggle?.name} » sera masquée partout (sélecteur, listes). Ses données sont conservées et vous pourrez la réactiver à tout moment.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="soc-toggle-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={toggleSocActive} data-testid="soc-toggle-confirm">
              {socToToggle?.active === false ? "Réactiver" : "Désactiver"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
